import React, { memo, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FiAlertCircle, FiChevronDown, FiExternalLink, FiFolder, FiSearch } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { formatDate, formatDateTime } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { BUTTON, CARD, EmptyState, INPUT, Pill, SIZE, SkeletonRows } from '../ui/kit';

const PAGE_SIZE = 30;

// Fields of the former patient list (Firestore collection "patients", before the current
// base), in the order of the former page.
const FIELDS = [
  ['pathologie', 'Pathologie'],
  ['diagnostic', 'Diagnostic'],
  ['anneeDossier', 'Année du dossier'],
  ['dateNaissance', 'Date de naissance'],
  ['sexe', 'Sexe'],
  ['taille', 'Taille'],
  ['poids', 'Poids'],
  ['adresse', 'Adresse'],
  ['addressDomicile', 'Domicile'],
  ['numeroDeTelephone', 'Téléphone'],
  ['addressEmail', 'Email'],
  ['salle', 'Salle'],
  ['lit', 'Lit'],
  ['correspondant', 'Correspondant'],
  ['dateEntree', 'Date d’entrée'],
  ['dateSortie', 'Date de sortie'],
  ['statut', 'Statut'],
  ['auteurEnregistrement', 'Dossier enregistré par'],
  ['commentaire', 'Commentaire'],
];

const text = (value) => (value === undefined || value === null ? '' : String(value).trim());

const Record = memo(({ record, open, onToggle }) => {
  const files = (Array.isArray(record.files) ? record.files : [])
    .map((file) => ({ ...(normalizeLinks([file])[0] || {}), timestamp: file && file.timestamp }))
    .filter((file) => file.link);
  const details = FIELDS.filter(([key]) => text(record[key]));
  return (
    <li className="border-0 border-b-[1px] border-solid border-black/[0.05] last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(record.id)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-[14px] border-0 bg-[transparent] px-[16px] py-[14px] text-left text-ink transition hover:bg-black/[0.02] sm:px-6"
      >
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-600" aria-hidden="true">
          <FiFolder size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold">{text(record.nom) || 'Nom non précisé'}</span>
          <span className="mt-0.5 block truncate text-[13px] text-black/45">
            {[record.dossierNumber && `Dossier ${record.dossierNumber}`, text(record.service), text(record.anneeDossier), text(record.pathologie)].filter(Boolean).join(' · ') || 'Sans numéro de dossier'}
          </span>
        </span>
        {files.length > 0 && <Pill>{files.length} fichier{files.length > 1 ? 's' : ''}</Pill>}
        <FiChevronDown size={17} aria-hidden="true" className={`flex-none text-black/30 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-[16px] pb-[18px] sm:px-6 sm:pl-[78px]">
          {details.length > 0 ? (
            <dl className="m-0 grid grid-cols-1 gap-x-[24px] gap-y-[10px] sm:grid-cols-2">
              {details.map(([key, label]) => (
                <div key={key} className="min-w-0">
                  <dt className="text-[12px] font-medium text-black/45">{label}</dt>
                  <dd className="m-0 whitespace-pre-wrap break-words text-[14px] text-ink">{text(record[key])}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="m-0 text-[14px] text-black/45">Aucune autre information dans ce dossier.</p>
          )}
          {files.length > 0 && (
            <ul className="mb-0 mt-[14px] flex list-none flex-col gap-1.5 p-0">
              {files.map((file, index) => (
                <li key={`${file.link}-${index}`}>
                  <a href={file.link} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1.5 break-all text-[14px] font-medium text-brand-600 no-underline hover:text-brand-700">
                    <FiExternalLink size={13} aria-hidden="true" className="flex-none" />
                    {file.name}
                  </a>
                  {formatDate(file.timestamp) && <span className="ml-1.5 text-[12px] text-black/40">ajouté le {formatDate(file.timestamp)}</span>}
                </li>
              ))}
            </ul>
          )}
          {record.lastModified && <p className="mb-0 mt-[12px] text-[12px] text-black/40">Dernière modification : {formatDateTime(record.lastModified)}</p>}
        </div>
      )}
    </li>
  );
});

// Former patient list, kept for consultation only.
const FormerRecords = () => {
  const [state, setState] = useState({ status: 'loading', records: [] });
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'patients'))
      .then((snapshot) => {
        if (cancelled) return;
        const records = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .map((record) => ({ ...record, search: [record.nom, record.dossierNumber, record.pathologie, record.diagnostic, record.service, record.anneeDossier].map(text).join(' ').toLowerCase() }))
          .sort((a, b) => text(a.nom).localeCompare(text(b.nom), 'fr'));
        setState({ status: 'ready', records });
      })
      .catch(() => !cancelled && setState({ status: 'error', records: [] }));
    return () => {
      cancelled = true;
    };
  }, []);

  const query = useDeferredValue(search.trim().toLowerCase());
  const shown = useMemo(
    () => (query ? state.records.filter((record) => query.split(/\s+/).every((word) => record.search.includes(word))) : state.records),
    [state.records, query]
  );
  useEffect(() => setLimit(PAGE_SIZE), [query]);
  const toggle = (id) => setOpen((current) => (current === id ? null : id));

  return (
    <>
      <p className="mb-0 mt-6 text-[14px] leading-relaxed text-black/50">
        Dossiers de l’ancienne liste des patients, d’avant la base actuelle. Ils sont consultables ici et ne se modifient plus.
      </p>
      <div className="relative mt-[14px]">
        <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nom, n° de dossier, pathologie, diagnostic…"
          aria-label="Rechercher dans les anciens dossiers"
          className={`${INPUT} h-11 pl-[42px]`}
        />
      </div>
      <section className={`${CARD} mt-[16px] overflow-hidden`}>
        {state.status === 'loading' ? (
          <div className="p-6">
            <SkeletonRows rows={5} />
          </div>
        ) : state.status === 'error' ? (
          <EmptyState icon={FiAlertCircle} title="Anciens dossiers indisponibles">
            Ils n’ont pas pu être chargés. Vérifiez la connexion, puis rechargez la page.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon={FiFolder} title="Aucun ancien dossier">
            {state.records.length ? 'Aucun dossier ne correspond à cette recherche.' : 'L’ancienne liste des patients est vide.'}
          </EmptyState>
        ) : (
          <>
            <ul className="m-0 list-none p-0">
              {shown.slice(0, limit).map((record) => (
                <Record key={record.id} record={record} open={open === record.id} onToggle={toggle} />
              ))}
            </ul>
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {Math.min(PAGE_SIZE, shown.length - limit)} dossiers de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} affichés
                </span>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default FormerRecords;
